import CmsCard from '@components/components/CmsComponents/components/CmsCard/CmsCard';
import styles from './CmsCardList.module.scss';
import { useMedia } from '@components/hooks';
import {
  Heading,
  Slider,
  SliderControl,
  getHeadingTag,
} from '@wienenergiegithub/ui-next';
import React, { Fragment } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

const CmsCardListSlider = ({
  title,
  cards,
  headingLevel = 3,
  isList = false,
  type,
  containerDimension,
}) => {
  const isMobile = useMedia('(max-width: 767px)');

  const defaultSettings = {
    spaceBetween: 24,
    slidesPerView: 1.2,
    breakpoints: {
      576: {
        slidesPerView: 2.2,
      },
      1000: {
        slidesPerView: 4,
      },
    },
  };

  const displaySlider = !isList || isMobile;

  return displaySlider ? (
    <Slider {...defaultSettings}>
      <div className={styles.controlsContainer} data-no-slide="true">
        <SliderControl className={styles.control} direction="left" />
        <SliderControl className={styles.control} direction="right" />
      </div>
      {cards?.map(card => (
        <CmsCard
          inSlider
          mainTitle={title}
          type={type}
          {...card}
          headingLevel={headingLevel}
          key={card?.action?.url}
        />
      ))}
    </Slider>
  ) : (
    <ul
      className={classNames(styles.flexList, {
        [styles.isInLargeContainer]: containerDimension === 'lg',
        [styles.isInMediumContainer]: containerDimension === 'md',
      })}
    >
      {cards?.map(card => (
        <Fragment key={card?.action?.url}>
          <CmsCard
            as="li"
            isFlexItem
            to={card?.action?.url}
            type={type}
            {...card}
            className={styles.flexListItem}
            containerDimension={containerDimension}
            headingLevel={headingLevel}
          />
        </Fragment>
      ))}
    </ul>
  );
};

CmsCardListSlider.propTypes = {
  // exact shape of card objects vary depending on card type
  // eslint-disable-next-line react/forbid-prop-types
  cards: PropTypes.arrayOf(PropTypes.object).isRequired,
  containerDimension: PropTypes.string,
  headingLevel: PropTypes.number,
  isList: PropTypes.bool,
  mainTitle: PropTypes.string,
  type: PropTypes.oneOf(['textSlidingCard', 'iconSlidingCard']).isRequired,
};

const CmsCardListStatic = ({
  cards,
  headingLevel = 3,
  type,
  containerDimension,
  className,
  titleTag,
}) => (
  <ul className={className}>
    {cards?.map(card => (
      <Fragment key={card?.id}>
        <CmsCard
          as="li"
          headingLevel={headingLevel}
          {...card}
          className={classNames(styles.listItem, styles[type], {
            [styles.isInContainer]:
              containerDimension && containerDimension !== 'full',
            [styles.hasAction]: card?.action,
          })}
          titleTag={titleTag}
          type={type}
        />
      </Fragment>
    ))}
  </ul>
);

CmsCardListStatic.propTypes = {
  // exact shape of card objects vary depending on card type
  // eslint-disable-next-line react/forbid-prop-types
  cards: PropTypes.arrayOf(PropTypes.object).isRequired,
  className: PropTypes.string,
  containerDimension: PropTypes.string,
  headingLevel: PropTypes.number,
  titleTag: PropTypes.string,
  type: PropTypes.oneOf([
    'helpCard',
    'infoCard',
    'contentCard',
    'imageCard',
    'choiceCard',
  ]).isRequired,
};

const CmsCardList = ({
  headingLevel = 3,
  title,
  titleTag,
  isList = false,
  type,
  cards,
  containerDimension,
}) => {
  const isMobile = useMedia('(max-width: 767px)');

  const renderCardElements = () => {
    if (!type) {
      return null;
    }
    if (type === 'textSlidingCard' || type === 'iconSlidingCard') {
      return (
        <CmsCardListSlider
          cards={cards}
          containerDimension={containerDimension}
          headingLevel={title ? headingLevel + 1 : headingLevel}
          isList={isList}
          title={title}
          titleTag={titleTag}
          type={type}
        />
      );
    }
    return (
      <CmsCardListStatic
        cards={cards}
        className={classNames(styles.staticList, styles[type], {
          [styles.twoChoiceCards]: type === 'choiceCard' && cards.length === 2,
          [styles.isInContainer]:
            containerDimension && containerDimension !== 'full',
        })}
        containerDimension={containerDimension}
        headingLevel={title ? headingLevel + 1 : headingLevel}
        titleTag={titleTag}
        type={type}
      />
    );
  };

  const isSlidingCard =
    type === 'textSlidingCard' || type === 'iconSlidingCard';

  const centerHeading = isSlidingCard && isList && !isMobile;

  return (
    <>
      {title && (
        <Heading
          align={centerHeading ? 'center' : null}
          as={getHeadingTag(headingLevel)}
          size="h2"
        >
          {title}
        </Heading>
      )}
      {renderCardElements()}
    </>
  );
};

CmsCardList.propTypes = {
  // exact shape of card objects vary depending on card type
  // eslint-disable-next-line react/forbid-prop-types
  cards: PropTypes.arrayOf(PropTypes.object).isRequired,
  containerDimension: PropTypes.string,
  isList: PropTypes.bool,
  title: PropTypes.string,
  titleTag: PropTypes.string,
  type: PropTypes.oneOf([
    'textSlidingCard',
    'iconSlidingCard',
    'helpCard',
    'infoCard',
    'contentCard',
    'imageCard',
    'choiceCard',
  ]).isRequired,
};

export default CmsCardList;
